<template>
  <div class="main" :style="{ width: width }">
    <div
      class="title"
      v-if="showTitle"
      :class="titleColor === 'secondary' ? 'secondary-title' : 'default-title'"
      :style="
        customTableColor
          ? customTableColor.isCustom
            ? customTableColor.headerStyle
            : ''
          : ''
      "
    >
      {{ title }}
    </div>
    <slot name="error" />
    <div
      v-if="!$slots.error"
      class="data-table"
      :style="
        customTableColor
          ? customTableColor.isCustom
            ? customTableColor.bodyStyle
            : ''
          : ''
      "
    >
      <div class="search-input" v-show="showSearch">
        <div
          class="search-btn"
          @click="toggleSearch"
          v-tooltip:top="'Close search'"
          :id="`search-toggle-${getIdfromTitle()}`"
        >
          <BaseIcon name="search" width="1rem" height="1rem" />
        </div>
        <input
          class="box-search"
          v-show="search"
          :placeholder="$t('search')"
          v-model="curr"
          ref="search"
          @input="onSearch"
          :id="`input-search-${getIdfromTitle()}`"
        />
        <button
          @click="clear"
          class="clear-btn"
          v-tooltip:top="'Clear search'"
          :id="`clear-search-${getIdfromTitle()}`"
        >
          <BaseIcon name="closeButton-mini" />
        </button>
      </div>
      <!-- /SEARCH -->
      <div class="top-container">
        <div
          v-for="(header, i) in headers"
          :key="i"
          :style="{ width: header.width }"
          class="header-width"
        >
          <div class="header-container" :style="header.style">
            <RexSearchSelect
              v-if="header.dropdown"
              :data="headerOptions"
              :current="currentOption"
              :objectValueIndex="1"
              :width="dropdownWidth"
              @onChoose="onOptionSelect"
              :isSearch="true"
              id="search-select-sm-metric-type"
            />
            <div
              class="header-table"
              :style="{ cursor: header.sortable ? 'pointer' : 'auto' }"
              v-else
            >
              {{ header.name }}
              <span
                class="search-btn"
                v-if="header.key === 'name' || header.isSearchable"
                @click="toggleSearch"
                :id="`search-toggle-open-${getIdfromTitle()}`"
              >
                <BaseIcon name="search" width="1rem" height="1rem" />
              </span>
            </div>

            <div
              :style="{ display: header.sortable ? 'flex' : 'none' }"
              :class="
                header.key != sortByKey
                  ? 'sort-buttons'
                  : 'sort-buttons-visible'
              "
            >
              <i
                @click="
                  sortByKey = header.key;
                  order = 'ASC';
                  clickSort(header.key, 'ASC');
                "
                :class="
                  header.key === sortByKey && order === 'ASC'
                    ? `selected-sort`
                    : `unselected-sort`
                "
                class="up"
                :id="`sort-${header.key}-asc-${getIdfromTitle()}`"
              >
                <BaseIcon name="arrowUpSort" width=".825rem" height="1rem" />
              </i>
              <i
                @click="
                  sortByKey = header.key;
                  order = 'DES';
                  clickSort(header.key, 'DES');
                "
                :class="
                  header.key === sortByKey && order === 'DES'
                    ? `selected-sort`
                    : `unselected-sort`
                "
                class="down"
                :id="`sort-${header.key}-des-${getIdfromTitle()}`"
              >
                <BaseIcon name="arrowDownSort" width=".825rem" height="1rem" />
              </i>
            </div>
          </div>
        </div>
      </div>

      <div class="data-container" :style="{ height: height }">
        <div v-if="items.length === 0 && !loading" class="container-no-data">
          <NoData :message="$t('noData')"></NoData>
        </div>
        <Loading v-if="loading" />
        <div v-for="(item, i) in items" :key="i" class="content-container">
          <div
            v-for="(header, j) in headers"
            :key="i + ' ' + j"
            :style="{
              width: header.width,
              background: i % 2 === 0 ? highlightColor : highlightColorDark,
              cursor:
                header.routable === false ||
                (header.key === ApplicationOverviewHeader.APM_INFRA &&
                  !item.isCorrelationAvailable)
                  ? cursorType.auto
                  : cursorType.pointer,
            }"
            class="content-row"
            :id="`redirect-${i}-${j}-${getIdfromTitle()}`"
            @click="
              redirectTo(item, header.routable, header.baseRoute, header.key)
            "
          >
            <div
              v-if="header.key === 'apdex'"
              class="semibold"
              :class="getClass(computeApdex(item[header.key]))"
            >
              {{ computeApdex(item[header.key]) }}
            </div>
            <div v-else-if="header.key === 'response'" class="colorHeading">
              {{ item[header.key] }}
            </div>
            <div
              v-else-if="
                aggregationNum &&
                (header.key === 'processRam' ||
                  header.key === 'processCpu' ||
                  header.key === 'cpu')
              "
            >
              {{ computeProcessData(item[header.key], aggregationNum) }}
            </div>
            <div
              class="infrastructure"
              v-else-if="header.key === ApplicationOverviewHeader.APM_INFRA"
            >
              <div v-if="item.isCorrelationAvailable">
                <BaseIcon
                  name="infrastructure"
                  width="2.2rem"
                  height="1.6rem"
                />
              </div>
              <div v-else>
                <BaseIcon
                  name="infrastructureDisable"
                  width="2.2rem"
                  height="1.6rem"
                  v-tooltip:top="$t('correlationNotAvailable')"
                />
              </div>
            </div>
            <div
              v-else
              :class="header.isEllipsisReq ? 'ellipsis-content' : ''"
              v-tooltip:top="{
                content: item[header.key],
                disabled: !header.isEllipsisReq || !item[header.key],
              }"
            >
              {{ item[header.key] }}
            </div>
            <div v-if="header.type === 'actions'" class="actions">
              <div
                v-for="(action, k) in actions"
                :key="`action-${k}`"
                class="loop"
              >
                <div v-if="action.type === 'edit'" class="content">
                  <button
                    class="action-btn"
                    @click="actionClick"
                    :id="`btn-${action.type}-${i}-${j}-${getIdfromTitle()}`"
                  >
                    <BaseIcon :name="action.icon" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="pagination" v-if="!hidePagination">
        <!-- v-if="items.length > 0" -->
        <RexPaginationV3
          @stateChange="(currState) => handlePageState(currState)"
          :total="total"
          size="small"
          :defaultPageNo="currentPage"
          :pageSizeOptions="defaultPageProps"
          :defaultPageSize="getDefaultPageSize"
        />
      </div>
    </div>
  </div>
</template>

<script>
// import RexPagination from './rex-pagination.vue';
import RexPaginationV3 from '@/rex-components/RexPaginationV3.vue';
import NoData from '@/rex-components/rex-no-data.vue';
import { parseApdex, parseProcessData } from '@/utils/helper-function';
import Loading from '@/rex-components/rex-loading.vue';
import { replaceSpaceInId } from '../utils/helper-function';
import RexSearchSelect from './RexSearchSelect.vue';
import { ApplicationOverviewHeader } from '@/utils/enums/headerType';
import { nextTick } from 'vue';

export default {
  components: { RexPaginationV3, NoData, Loading, RexSearchSelect },
  props: {
    /**
     *
     *If there is changes in title color of the table
     *
     */
    titleColor: { type: String, default: 'primary' },
    /**
     *
     *If there is Title of the table
     *
     */
    showTitle: { type: Boolean, default: true },
    /**
     *
     *Title of the table
     *
     */
    title: { type: String, required: true },
    /**
     *
     *The columns for the table with information about the widths of the columns, sortable, default sort and order
     * @example
      headers: [
        {
          key: name,
          name: this.$t('name'),
          width: '36%',
          sortable: false
        },
        {
          key: alerts,
          name: this.$t('alerts'),
          width: '14%',
          sortable: true,
          dataType: 'float',
          defaultOrder: DES,
          defaultSortBy: true
        },
        ]
     *
     */
    headers: { type: Array, required: true },
    /**
     *
     *Data of the table to be displayed
     *
     */
    items: { type: Array, required: true },
    /**
     *
     *If the table has search function
     *
     */
    search: { type: Boolean, default: true },
    /**
     *
     *If table has actions on row elements
     *
     */
    actions: { type: Boolean, default: false },
    /**
     *
     *The accent color of the table on entries
     *
     */
    highlight: { type: String, default: 'primary' },
    /**
     *
     *Height of the table
     *
     */
    height: { type: String, required: true },
    /**
     *
     *Width of the table as percentage of the page
     *
     */
    width: { type: String, required: true },
    /**
     *
     *Total number of rows in the table
     *
     */
    total: { type: Number },
    /**
     *
     *For adding custom pagination options
     @example
      getDefaultPageProps() {
      return ['15', '30','50']
        }
      ];
    }
     *
     */
    defualtPageProps: {},
    /**
     *
     *For component level loading
     *
     */
    loading: { type: Boolean },
    /**
     *
     *For current option for dropdown header
     *
     */
    currentOption: { type: Object },
    /**
     *
     *For adding options for dropdown header
     *
     */
    headerOptions: { type: Array },
    /**
     *
     *Dropdown width for the dropdown header
     *
     */
    dropdownWidth: { type: String, default: '14rem' },
    /**
     *
     *Option to hide pagination
     */
    hidePagination: {
      type: Boolean,
      default: false,
    },
    /**
     *
     *Prop for table background color
     *  customTableColor : {
     *    isCustom : true,
     *    headerStyle : {
     *      'backgroundColor' : 'red'
     *    },
     *    bodyStyle : {
     *      'backgroundColor' : 'red'
     *    }
     * }
     */

    customTableColor: { type: Object, default: () => {} },
    /**
     * Prop to turn on/off alternate row colors
     */
    coloredRows: { type: Boolean, default: true, required: false },
    aggregationNum: {
      type: Number,
    },
  },
  data() {
    return {
      curr: '',
      sortByKey: '',
      order: '',
      dataType: '',
      showSearch: false,
      currentPage: this.pageNo,
      defaultPageProps: this.checkIfDefaultPagePropsUndefined(
        this.defualtPageProps,
      ),
      ApplicationOverviewHeader,
      cursorType: {
        pointer: 'pointer',
        auto: 'auto',
      },
    };
  },
  computed: {
    highlightColor() {
      if (!this.coloredRows) return '#2e3032';
      return this.highlight === 'primary' ? 'rgb(41,44,50)' : 'rgb(41,44,50)';
    },
    highlightColorDark() {
      if (!this.coloredRows) return '#2e3032';
      return 'rgb(30,32,33)';
    },
    getDefaultPageSize() {
      return this.defaultPageProps && parseInt(this.defaultPageProps[0]);
    },
  },
  methods: {
    redirectTo(option, isRoutable, to, headerKey) {
      if (isRoutable === false) {
        /**
         * Dont route if this is false
         * If this key is not present
         * stick to default behaviour
         */
        return;
      }
      if (
        headerKey === ApplicationOverviewHeader.APM_INFRA &&
        !option.isCorrelationAvailable
      ) {
        return;
      }
      /**
       * Adding route for that column to options
       * so that they can be used later on for routing to some specific route
       */
      option.redirectTo = to;
      this.$emit('redirectInput', option);
    },
    onOptionSelect(option) {
      this.$emit('selectInput', option);
    },
    clickSort(col, order) {
      let item = { header: col, order: order };
      this.$emit('updateSearch', item);
    },
    onSearch() {
      // console.log(this.curr);
      this.$emit('onSearchChange', this.curr);
    },
    handlePageState(state) {
      this.currentPage = state.pageNo;
      this.$emit('onPageChange', state);
    },
    computeApdex(value) {
      return parseApdex(value);
    },
    computeProcessData(value, aggregationNum) {
      return parseProcessData(value, aggregationNum);
    },
    getClass(value) {
      if (value <= 1.0 && value >= 0.84) {
        return 'good';
      } else if (value <= 0.83 && value >= 0.69) {
        return 'fair';
      } else if (value <= 0.68 && value >= 0) {
        return 'poor';
      } else return 'none';
    },
    toggleSearch() {
      this.showSearch = !this.showSearch;
      //put cursor on search input box
      if (this.$refs.search) {
        nextTick(() => this.$refs.search.focus());
      }
    },
    mouseOver: function () {
      this.active = !this.active;
    },
    intiDefaultSort() {
      for (let i in this.headers) {
        let header = this.headers[i];
        if (header.defaultSortBy) {
          this.sortByKey = header.key;
          this.order = header.defaultOrder;
        }
      }
    },
    clear() {
      this.curr = '';
      this.$emit('onClear');
    },
    checkIfDefaultPagePropsUndefined(input) {
      if (input === undefined) {
        return [
          {
            defaultVal: '10',
            props: [
              { value: '10', name: '10' },
              { value: '20', name: '20' },
              { value: '50', name: '50' },
            ],
          },
        ];
      }
      return input;
    },
    getIdfromTitle() {
      return replaceSpaceInId(this.title.toLowerCase());
    },
  },
  watch: {
    pageNo(value) {
      this.currentPage = value;
    },
  },
  created() {
    this.intiDefaultSort();
  },
};
</script>

<style lang="scss" scoped>
.main {
  @include dropshadow-effect();
  border-radius: 1.0625rem;
  height: 100%;
}
.title {
  text-align: left;
  height: 2.375rem;
  background-color: $bg-tertiary;
  border-top-left-radius: 1.0625rem;
  border-top-right-radius: 1.0625rem;
  padding-top: 0.625rem;
  padding-left: 1.5rem;
  font-size: 1.125rem;
  font-family: $bold;
  color: $font-primary;
  padding: 0.4rem 0 0.7rem 1.25rem;
}
.secondary-title {
  background-color: $bg-secondary;
  font-size: 1rem;
  font-family: $semi-bold !important;
  border-bottom: 0.0125rem solid $border-primary;
}
.data-table {
  border-bottom-left-radius: 1.0625rem;
  border-bottom-right-radius: 1.0625rem;
  background-color: $bg-secondary;
  //  border: 0.0063rem $border-primary solid;
  border-top: none;
  // padding: 0rem 1rem;
}
.top-container {
  position: relative;
  color: $color-brand-accent;
  display: flex;
}
.header-container {
  color: $color-brand-accent;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border-bottom: 0.0125rem solid $border-primary;
  height: 2.8rem;
  padding: 0rem 1rem;
  margin-bottom: 1rem;
  text-align: left;

  .header-table {
    //color: $font-primary;
    font-family: $semi-bold;
    font-size: 1rem;
  }

  .sort-buttons-visible {
    display: flex;
    flex-direction: row;
    justify-content: space-evenly;
    position: relative;
    cursor: pointer;
  }
  .sort-buttons {
    display: flex;
    flex-direction: row;
    justify-content: space-evenly;
    position: relative;
    cursor: pointer;
    visibility: hidden;
  }
}
.container-section:first-child > .content-full {
  padding-left: 1rem;
}
.container-section:last-child > .content-full {
  padding-right: 1rem;
}
.data-container {
  position: relative;
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
  .content-container {
    display: flex;
    .content-row {
      display: flex;
      align-items: center;
      height: 3rem;
      margin: 0.45em 0em 0.45em 0em;
      padding: 0em 1.175em;
      text-align: left;
      cursor: pointer;
      padding: {
        top: 0.3rem;
        bottom: 0.3rem;
      }
      .semibold {
        font-family: $bold;
      }
      .good {
        color: $good;
      }
      .poor {
        color: $poor;
      }
      .fair {
        color: $fair;
      }
      .none {
        color: $font-primary;
      }
      .primary {
        background-color: $highlight-primary;
      }
      .secondary {
        background-color: $highlight-secondary;
      }
      .colorHeading {
        color: $font-primary;
      }
    }
  }
  // CONTENT
}
.search-btn {
  margin: 0px 0.625rem;
  @include svg-fill-pointer($color-brand-accent);
}
.search-input {
  position: absolute;
  display: flex;
  height: 2.25rem;
  width: 17rem;
  border-radius: 0.1875rem;
  border: 0.0625rem $color-brand-accent solid;
  background-color: $bg-primary;
  z-index: 4;
  margin-left: 1.25rem;
  margin-top: 0.25rem;
  padding: 0.3rem 0rem;
  input {
    border: none;
    caret-color: $color-brand-accent;
    color: $font-primary;
    background: transparent;
  }
  textarea:focus,
  input:focus {
    outline: none;
  }
}
.pagination {
  height: 3.75rem;
  display: flex;
  justify-content: flex-end;
  margin: 1.25rem 0.875rem;
  margin-bottom: 0rem;
}
.action-btn {
  @include btn-reset;
  fill: $font-primary;
}
.header-container:hover > .sort-buttons {
  visibility: visible;
}
.selected-sort {
  z-index: 1;
  fill: $color-brand-accent;
}
.unselected-sort {
  fill: $color-brand-accent-inactive;
  z-index: 2;
}

.up {
  margin-left: 0.5rem;
}

.down {
  margin-right: 0.5rem;
}
.header-width:first-child > .header-container {
  padding-left: 1.25rem;
}
.clear-btn {
  height: 100%;
  background: transparent;
  border: transparent;
  fill: $color-brand-accent;
  margin-left: auto;
}
.container-no-data {
  width: 100%;
  height: 100%;
}
.ellipsis-content {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.default-title {
  background-color: $bg-secondary;
  font-family: $semi-bold !important;
  border-bottom: 0.0125rem solid $border-primary;
}
.infrastructure {
  margin-top: 0.35em;
}
</style>
